import { SEED_SCHEMA_VERSION } from '@swarasense/shared';
import type { RagaRecord, SeedBundle } from '@swarasense/shared';
import { rankRagaMatches } from './ragam-search.js';

/**
 * Static seed bundle client: an offline-first, alias-aware raga lookup fed by
 * the versioned `/seed/carnatic-seed.json` static asset (SW-cached) with a
 * durable IndexedDB cache underneath, so raga-aware local coaching works
 * immediately on repeat visits and offline (FR-12, NFR-R1). No DynamoDB, no
 * network required after the first load.
 */

const DB_NAME = 'swarasense';
const STORE_NAME = 'seed';
const BUNDLE_URL = '/seed/carnatic-seed.json';

interface CachedSeed {
  data_version: string;
  bundle: SeedBundle;
}

let ragaIndex = new Map<string, RagaRecord>();
/** The bundle's raga records, each appearing exactly once (unlike `ragaIndex`,
 * whose values repeat once per alias) — backs `searchRagas`. */
let ragaList: RagaRecord[] = [];

/** Tiny promisified IndexedDB wrapper — deliberately minimal, no new dependency. */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME))
        req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error as unknown);
  });
}

/** The single cached entry, if any (the store only ever holds the latest data_version). */
function readCached(db: IDBDatabase): Promise<CachedSeed | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve((req.result as CachedSeed[])[0]);
    req.onerror = () => reject(req.error as unknown);
  });
}

/** Replace the store contents with a single entry, keyed on data_version (prunes anything older). */
function writeCached(db: IDBDatabase, entry: CachedSeed): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.clear();
    store.put(entry, entry.data_version);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error as unknown);
  });
}

/** Alias-aware, case-insensitive lookup index: `name`/`aliases` (lowercased) → record. */
function buildIndex(bundle: SeedBundle): Map<string, RagaRecord> {
  const map = new Map<string, RagaRecord>();
  for (const raga of bundle.ragas) {
    map.set(raga.name.toLowerCase(), raga);
    for (const alias of raga.aliases) map.set(alias.toLowerCase(), raga);
  }
  return map;
}

/** Fetch the static bundle; on a newer, schema-supported bundle, swap the in-memory index and persist it. */
async function refresh(
  db: IDBDatabase | undefined,
  knownVersion: string | undefined,
): Promise<void> {
  try {
    const res = await fetch(BUNDLE_URL);
    if (!res.ok) return;
    const bundle = (await res.json()) as SeedBundle;
    if (bundle.schema_version !== SEED_SCHEMA_VERSION) return;
    if (bundle.data_version === knownVersion) return;
    ragaIndex = buildIndex(bundle);
    ragaList = bundle.ragas;
    if (db) {
      try {
        await writeCached(db, { data_version: bundle.data_version, bundle });
      } catch {
        // Best-effort durable cache; the in-memory index is already up to date.
      }
    }
  } catch {
    // Network/parse failure — keep whatever's already in memory.
  }
}

let loadPromise: Promise<void> | undefined;

/**
 * Load the seed bundle: offline-first from IndexedDB (populates the in-memory
 * index immediately if a supported cached bundle exists), then refreshes from
 * the network in the background. On first-ever load (nothing cached yet) the
 * fetch runs synchronously so coaching has data right away. Never throws —
 * any failure just leaves the in-memory index as-is (possibly empty).
 *
 * Memoized: callers (e.g. `app.ts` at startup, `setup.ts` wanting to know when
 * the ragam picker has data) can all call this freely — the actual load work
 * runs once, and everyone awaits the same promise.
 */
export function loadSeedBundle(): Promise<void> {
  if (!loadPromise) loadPromise = loadSeedBundleOnce();
  return loadPromise;
}

async function loadSeedBundleOnce(): Promise<void> {
  let db: IDBDatabase | undefined;
  try {
    db = await openDb();
  } catch {
    db = undefined;
  }

  let cached: CachedSeed | undefined;
  if (db) {
    try {
      cached = await readCached(db);
    } catch {
      cached = undefined;
    }
  }

  if (cached && cached.bundle.schema_version === SEED_SCHEMA_VERSION) {
    ragaIndex = buildIndex(cached.bundle);
    ragaList = cached.bundle.ragas;
    void refresh(db, cached.data_version);
    return;
  }

  await refresh(db, undefined);
}

/** Look up a raga record by name or alias, case-insensitively. */
export function ragaSeedFor(name: string): RagaRecord | undefined {
  return ragaIndex.get(name.toLowerCase());
}

/**
 * Name/alias search over the loaded ragas, ranked (prefix over substring,
 * case-insensitive — see `rankRagaMatches`). Empty query returns all records.
 * `limit` caps the result count for a picker UI (default 25).
 */
export function searchRagas(query: string, limit = 25): RagaRecord[] {
  return rankRagaMatches(query, ragaList).slice(0, limit);
}

/**
 * Every loaded raga record, unranked and uncapped (content SEO pages, Task 5:
 * the `?raga=` deep-link needs the FULL list to check against — `searchRagas`
 * caps at `limit` (default 25), which would silently make a real raga
 * unresolvable from a deep link just because of its rank/position in the
 * bundle). Empty before `loadSeedBundle()` resolves, exactly like
 * `ragaSeedFor`/`searchRagas`.
 */
export function allRagaRecords(): RagaRecord[] {
  return ragaList;
}
